import { useCallback, useState } from 'react';
import { Option } from 'models/Global';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

const EMPTY_ARRAY: Option[] = [];

export default function useSearchableFilters(
  getOptionsOnSearch: (searchString: string) => Promise<Option[]>,
  initialOptions: Option[] = EMPTY_ARRAY,
) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [options, setOptions] = useState<Option[]>(initialOptions);

  const getMixpanelProps = useGetMixpanelProps();

  const resetState = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
    setOptions(initialOptions);
  }, [initialOptions]);

  const onSearch = useCallback(
    async (searchString: string) => {
      if (!searchString) {
        resetState();
        return;
      }

      setIsLoading(true);
      setHasError(false);

      try {
        const options = await getOptionsOnSearch(searchString);

        setOptions(options);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
        setHasError(true);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [resetState, getOptionsOnSearch, getMixpanelProps],
  );

  return { isLoading, hasError, options, onSearch, resetState };
}
